import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, ArrowLeft, Upload, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { DatePickerField } from "@/components/DatePickerField";
import { waLink } from "@/constants/site";

const ITEMS = ["Dining Table", "Center Table", "Side Table", "Kitchen Countertop", "Sofa", "Curtain", "Rug", "Furniture", "Marble/Stone", "Glass", "Other"];
const PROPERTY = ["Apartment", "Villa", "House", "Office", "Hotel", "Restaurant", "Retail", "Other"];
const TIMES = ["09:00 AM", "11:00 AM", "01:00 PM", "03:00 PM", "05:00 PM"];
const STEPS = ["Protect", "Property", "Location", "Photos", "Contact"];

export default function ConsultationFlow() {
  const location = useLocation();
  const navigate = useNavigate();
  const pre = location.state || {};
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    item: pre.preferred_service || "", property_type: "", city: "", pincode: "",
    name: "", mobile: "", email: "", preferred_date: "", preferred_time: "",
  });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const upload = async (files) => {
    for (const file of Array.from(files)) {
      const fd = new FormData(); fd.append("file", file);
      try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPhotos((p) => [...p, data.url]); }
      catch (_) { toast.error("Upload failed"); }
    }
  };

  const submit = async () => {
    if (!f.name || !f.mobile) { toast.error("Name and phone are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/quotes", {
        name: f.name, mobile: f.mobile, email: f.email, location: f.city, pincode: f.pincode,
        property_type: f.property_type, surface_type: f.item, preferred_service: f.item,
        preferred_date: f.preferred_date, preferred_time: f.preferred_time, photos, source: pre.source || "Website",
      });
      track("consultation_submitted", { item: f.item });
      setDone(data);
    } catch (_) { toast.error("Could not submit. Please try again."); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="consultation-success">
        <CheckCircle2 className="mx-auto text-[#C9A45C]" size={56} />
        <h1 className="serif-head text-4xl mt-6">Consultation requested</h1>
        <p className="mt-4 text-gray-400">Thank you, {f.name}. Your request for <span className="text-white">{f.item || "surface protection"}</span> is in the ERRAH system. Our team will reach out shortly.</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a href={waLink(`Hi ERRAH, I just requested a consultation for ${f.item}.`)} target="_blank" rel="noopener noreferrer" className="bg-[#C9A45C] text-black px-7 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">Chat on WhatsApp</a>
          <button onClick={() => navigate("/")} className="border border-white/20 px-7 py-3.5 text-sm hover:bg-white hover:text-black transition-colors">Back to home</button>
        </div>
      </div>
    );
  }

  const canNext = [f.item, f.property_type, true, true, f.name && f.mobile][step];

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-10 py-14" data-testid="consultation-page">
      <Seo title="Book a Consultation" description="Book a free ERRAH surface protection consultation." />
      <p className="errah-label mb-3">Book a Consultation</p>
      <h1 className="serif-head text-4xl lg:text-5xl mb-8">Let's protect what matters.</h1>

      <div className="flex gap-2 mb-10">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1 ${i <= step ? "bg-[#C9A45C]" : "bg-white/10"}`} />
            <p className={`errah-label mt-2 !text-[10px] ${i === step ? "!text-[#C9A45C]" : "!text-gray-600"}`}>{s}</p>
          </div>
        ))}
      </div>

      {step === 0 && (
        <Step title="What would you like to protect?">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {ITEMS.map((o) => <Choice key={o} active={f.item === o} onClick={() => { set("item", o); next(); }} testid={`c-item-${o}`}>{o}</Choice>)}
          </div>
        </Step>
      )}
      {step === 1 && (
        <Step title="Property type">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {PROPERTY.map((o) => <Choice key={o} active={f.property_type === o} onClick={() => { set("property_type", o); next(); }} testid={`c-prop-${o}`}>{o}</Choice>)}
          </div>
        </Step>
      )}
      {step === 2 && (
        <Step title="Where are you located?">
          <div className="grid md:grid-cols-2 gap-4">
            <input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="City" data-testid="c-city" className="bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
            <input value={f.pincode} onChange={(e) => set("pincode", e.target.value)} placeholder="PIN code" data-testid="c-pincode" className="bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
          </div>
        </Step>
      )}
      {step === 3 && (
        <Step title="Upload photos (optional)">
          <div className="flex flex-wrap gap-3">
            {photos.map((url, i) => (
              <div key={i} className="relative w-24 h-24 hair-border overflow-hidden">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" />
                <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black/70 p-0.5"><X size={12} /></button>
              </div>
            ))}
            <label className="w-24 h-24 hair-border flex flex-col items-center justify-center cursor-pointer hover:border-[#C9A45C] transition-colors" data-testid="c-upload">
              <Upload size={18} className="text-gray-500" /><span className="text-[10px] text-gray-500 mt-1">Add</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
            </label>
          </div>
        </Step>
      )}
      {step === 4 && (
        <Step title="Your contact details">
          <div className="grid md:grid-cols-2 gap-4">
            <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Name *" data-testid="c-name" className="bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
            <input value={f.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="Phone *" data-testid="c-mobile" className="bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
            <input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" data-testid="c-email" className="bg-transparent border border-white/15 px-4 py-3 text-sm focus:border-[#C9A45C]" />
            <DatePickerField value={f.preferred_date} onChange={(v) => set("preferred_date", v)} testid="c-date" />
            <div className="md:col-span-2 flex flex-wrap gap-2">
              {TIMES.map((t) => <button key={t} onClick={() => set("preferred_time", t)} className={`px-4 py-2 text-sm border transition-colors ${f.preferred_time === t ? "bg-[#C9A45C] text-black border-[#C9A45C]" : "border-white/15 text-gray-300 hover:border-[#C9A45C]/60"}`}>{t}</button>)}
            </div>
          </div>
        </Step>
      )}

      <div className="mt-10 flex items-center justify-between">
        <button onClick={back} disabled={step === 0} className={`inline-flex items-center gap-2 text-sm ${step === 0 ? "text-gray-700" : "text-gray-400 hover:text-white"}`}><ArrowLeft size={16} /> Back</button>
        {step < STEPS.length - 1 ? (
          <button onClick={next} disabled={!canNext} data-testid="c-next" className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-7 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors disabled:opacity-40">Continue <ArrowRight size={16} /></button>
        ) : (
          <button onClick={submit} disabled={busy} data-testid="c-submit" className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-7 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors disabled:opacity-60">{busy ? "Submitting…" : <>Submit <Check size={16} /></>}</button>
        )}
      </div>
    </div>
  );
}

function Step({ title, children }) {
  return <div className="fade-up"><h2 className="serif-head text-2xl lg:text-3xl mb-6">{title}</h2>{children}</div>;
}
function Choice({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-5 py-5 text-left text-sm border transition-colors ${active ? "bg-[#C9A45C] text-black border-[#C9A45C]" : "border-white/15 bg-warmblack text-gray-200 hover:border-[#C9A45C]/60"}`}>
      {children}
    </button>
  );
}
